import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED } from "../../consts";

interface OverlayState {
  /** Drives the single full-screen loading Backdrop rendered by OverlayWrapper. */
  loading: boolean;
  /** Ids of "unique" snacks already shown, so they are only shown once per app session. */
  shownSnackList: string[];
  setLoading: (loading: boolean) => void;
  /**
   * Read-after-write dedupe guard for unique snacks, as one atomic action.
   *
   * Returns `true` when `id` had not been shown yet (and records it), `false` when it had.
   * The signals version did the `includes` check and the append as two separate statements
   * in `defaultAddSnack`; keeping them in a single action means they cannot drift apart.
   * Both `getState()` and `set()` are synchronous, so the semantics are unchanged.
   */
  markSnackShown: (id: string) => boolean;
  /** Clears the shown list — the app-resume reset in appStateListener. */
  resetShownSnackList: () => void;
}

// Replaces the `loadingSignal` / `shownedSnackListSignal` preact signals.
//
// `create` is imported from "zustand" directly and NOT from feat/appState/resetState.
// That wrapper registers a store for `resetAllStores()` on logout; neither of these
// signals was reset on logout, and clearing `shownSnackList` there would let
// once-per-session snacks reappear. No `persist` either — both were in-memory only.
export const useOverlayStore = create<OverlayState>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        loading: false,
        shownSnackList: [] as string[],
        setLoading: (loading: boolean) => set({ loading }, false, "setLoading"),
        markSnackShown: (id: string) => {
          if (get().shownSnackList.includes(id)) return false;
          set({ shownSnackList: [...get().shownSnackList, id] }, false, "markSnackShown");
          return true;
        },
        resetShownSnackList: () => set({ shownSnackList: [] }, false, "resetShownSnackList"),
      }),
      { enabled: DEVTOOLS_ENABLED, name: "overlay" },
    ),
  ),
);
